const style = (name, cssRules = name, defaultValue) => {
  const rules = !Array.isArray(cssRules) ? [cssRules] : cssRules;

  return (value = defaultValue) => props => {
    if (!Object.prototype.hasOwnProperty.call(props, name)) {
      return undefined;
    }

    return rules.map(rule => {
      let val;

      if (typeof props[name] === 'function') {
        val = props[name](props.theme);
      } else {
        val =
          (typeof value === 'function' ? value(props, name, rule) : value) ||
          props[name];
      }

      return `${rule}: ${val};`;
    });
  };
};

const setShow = ({ show }) => {
  if (!show) {
    return 'none';
  }
  return typeof show === 'string' ? show : 'block';
};

const setVisible = ({ visible }) => (visible ? 'visible' : 'hidden');

const setFromTheme = (themeName, themeDefault = 'normal') => (props, name) => {
  const theme = props.theme[themeName] || {};
  const value = props[name];
  if (typeof value === 'boolean') {
    return value ? theme[themeDefault] : undefined;
  }
  return theme[value] || value;
};

const setPaddingFromTheme = setFromTheme('padding');
const setMarginFromTheme = setFromTheme('margin');

const styles = {
  display: style('display'),
  show: () => style('show', 'display', setShow)(),
  visibility: style('visibility'),
  visible: () => style('visible', 'visibility', setVisible)(),
  width: style('width'),
  minWidth: style('minWidth', 'min-width'),
  maxWidth: style('maxWidth', 'max-width'),
  height: style('height'),
  minHeight: style('minHeight', 'min-height'),
  maxHeight: style('maxHeight', 'max-height'),
  position: style('position'),
  top: style('top'),
  right: style('right'),
  bottom: style('bottom'),
  left: style('left'),
  zIndex: style('zIndex', 'z-index'),
  overflow: style('overflow'),
  overflowX: style('overflowX', 'overflow-x'),
  overflowY: style('overflowY', 'overflow-y'),
  padding: style('padding', 'padding', setPaddingFromTheme),
  paddingTop: style('paddingTop', 'padding-top', setPaddingFromTheme),
  paddingRight: style('paddingRight', 'padding-right', setPaddingFromTheme),
  paddingBottom: style('paddingBottom', 'padding-bottom', setPaddingFromTheme),
  paddingLeft: style('paddingLeft', 'padding-left', setPaddingFromTheme),
  paddingHorizontal: style(
    'paddingHorizontal',
    ['padding-left', 'padding-right'],
    setPaddingFromTheme
  ),
  paddingVertical: style(
    'paddingVertical',
    ['padding-top', 'padding-bottom'],
    setPaddingFromTheme
  ),
  margin: style('margin', 'margin', setMarginFromTheme),
  marginTop: style('marginTop', 'margin-top', setMarginFromTheme),
  marginRight: style('marginRight', 'margin-right', setMarginFromTheme),
  marginBottom: style('marginBottom', 'margin-bottom', setMarginFromTheme),
  marginLeft: style('marginLeft', 'margin-left', setMarginFromTheme),
  marginHorizontal: style(
    'marginHorizontal',
    ['margin-left', 'margin-right'],
    setMarginFromTheme
  ),
  marginVertical: style(
    'marginVertical',
    ['margin-top', 'margin-bottom'],
    setMarginFromTheme
  ),
  backgroundColor: style(
    'backgroundColor',
    'background-color',
    setFromTheme('background')
  ),
  backgroundImage: style('backgroundImage', 'background-image'),
  backgroundPosition: style('backgroundPosition', 'background-position'),
  backgroundRepeat: style('backgroundRepeat', 'background-repeat'),
  backgroundSize: style('backgroundSize', 'background-size'),
  boxShadow: style('boxShadow', 'box-shadow'),
  flexDirection: style('flexDirection', 'flex-direction'),
  alignItems: style('alignItems', 'align-items'),
  alignContent: style('alignContent', 'align-content'),
  justifyContent: style('justifyContent', 'justify-content'),
  flexWrap: style('flexWrap', 'flex-wrap'),
  flex: style('flex'),
  alignSelf: style('alignSelf', 'align-self'),
  border: style('border'),
  borderTop: style('borderTop', 'border-top'),
  borderRight: style('borderRight', 'border-right'),
  borderBottom: style('borderBottom', 'border-bottom'),
  borderLeft: style('borderLeft', 'border-left'),
  borderColor: style('borderColor', 'border-color', setFromTheme('border')),
  borderRadius: style('borderRadius', 'border-radius'),
  borderBottomLeftRadius: style(
    'borderBottomLeftRadius',
    'border-bottom-left-radius'
  ),
  borderBottomRightRadius: style(
    'borderBottomRightRadius',
    'border-bottom-right-radius'
  ),
  borderTopLeftRadius: style('borderTopLeftRadius', 'border-top-left-radius'),
  borderTopRightRadius: style(
    'borderTopRightRadius',
    'border-top-right-radius'
  ),
  opacity: style('opacity'),
  transition: style('transition'),
  transform: style('transform'),
  transformOrigin: style('transformOrigin', 'transform-origin'),
  fontFamily: style('fontFamily', 'font-family', setFromTheme('font')),
  fontSize: style('fontSize', 'font-size', setFromTheme('fontSize')),
  fontWeight: style('fontWeight', 'font-weight'),
  color: style('color', 'color', setFromTheme('text')),
  textAlign: style('textAlign', 'text-align'),
  lineHeight: style('lineHeight', 'line-height'),
  whiteSpace: style('whiteSpace', 'white-space'),
  textOverflow: style('textOverflow', 'text-overflow'),
  textDecoration: style('textDecoration', 'text-decoration'),
  iconSize: style('iconSize', 'font-size', setFromTheme('iconSize')),
  iconColor: style('iconColor', 'color', setFromTheme('iconColor')),
  objectFit: style('objectFit', 'object-fit'),
  animation: style('animation', 'animation'),
};

const group = keys => callback => props =>
  keys.map(key => styles[key](callback)(props));

const display = group(['display', 'show', 'visibility', 'visible']);

const dimension = group([
  'width',
  'minWidth',
  'maxWidth',
  'height',
  'minHeight',
  'maxHeight',
]);

const position = group([
  'position',
  'top',
  'right',
  'bottom',
  'left',
  'zIndex',
]);

const overflow = group(['overflow', 'overflowX', 'overflowY']);

const padding = group([
  'padding',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'paddingHorizontal',
  'paddingVertical',
]);

const margin = group([
  'margin',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'marginHorizontal',
  'marginVertical',
]);

const background = group([
  'backgroundColor',
  'backgroundImage',
  'backgroundPosition',
  'backgroundRepeat',
  'backgroundSize',
  'boxShadow',
]);

const flexParent = group([
  'flexDirection',
  'alignItems',
  'alignContent',
  'justifyContent',
  'flexWrap',
]);

const flexChild = group(['flex', 'alignSelf']);

const border = group([
  'border',
  'borderColor',
  'borderTop',
  'borderRight',
  'borderBottom',
  'borderLeft',
  'borderRadius',
  'borderBottomLeftRadius',
  'borderBottomRightRadius',
  'borderTopLeftRadius',
  'borderTopRightRadius',
]);

const animation = group([
  'opacity',
  'transition',
  'transform',
  'transformOrigin',
  'animation',
]);

const font = group([
  'fontFamily',
  'fontSize',
  'fontWeight',
  'color',
  'textAlign',
  'lineHeight',
  'whiteSpace',
  'textOverflow',
  'textDecoration',
]);

const icon = group(['iconSize', 'iconColor']);

const image = group(['objectFit']);

export {
  styles,
  display,
  dimension,
  position,
  overflow,
  padding,
  margin,
  background,
  flexParent,
  flexChild,
  border,
  animation,
  font,
  icon,
  image,
};
